import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

import Swal from 'sweetalert2';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2
} from 'lucide-react';

function IndemnityFormSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [campaignSearchTerm, setCampaignSearchTerm] = useState('All Campaigns');
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const campaignInputRef = useRef(null);
  const campaignDropdownRef = useRef(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;
  
  // Detail modal
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [currentPage, selectedCampaign, searchTerm]);

  // Filter campaigns based on search term (treat "All Campaigns" as no filter)
  const isAllCampaigns = !campaignSearchTerm.trim() || campaignSearchTerm.trim() === 'All Campaigns';
  const filteredCampaigns = (() => {
    if (isAllCampaigns) {
      // When no search term or "All Campaigns", show all campaigns (limited to 50 for performance)
      return campaigns.slice(0, 50);
    }
    const search = campaignSearchTerm.toLowerCase();
    return campaigns.filter(campaign => 
      campaign.name?.toLowerCase().includes(search)
    ).slice(0, 10); // Limit to 10 results when searching
  })();

  // Handle clicks outside campaign dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        campaignDropdownRef.current && 
        !campaignDropdownRef.current.contains(event.target) && 
        campaignInputRef.current && 
        !campaignInputRef.current.contains(event.target)
      ) {
        setShowCampaignDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/admin/campaigns', { params: { limit: 500 } });
      // API returns { campaigns: [...] }
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setCampaigns([]); // Set empty array on error to prevent map errors
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: currentPage,
        limit: PAGE_SIZE
      };
      
      if (selectedCampaign) {
        params.campaignId = selectedCampaign;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await api.get('/admin/indemnity-forms/submissions/all', { params });
      setSubmissions(response.data.submissions || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
      setLoading(false);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Fetch submissions error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load submissions';
      setError(errorMessage);
      setSubmissions([]); // Set empty array on error
      setTotalPages(1);
      setTotal(0);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const handleCampaignSelect = (campaignId, campaignName) => {
    setSelectedCampaign(campaignId);
    setCampaignSearchTerm(campaignName);
    setShowCampaignDropdown(false);
    setCurrentPage(1);
  };

  const handleCampaignClear = () => {
    setSelectedCampaign('');
    setCampaignSearchTerm('All Campaigns');
    setShowCampaignDropdown(false);
    setCurrentPage(1);
  };

  const handleCampaignInputChange = (e) => {
    const value = e.target.value;
    setCampaignSearchTerm(value);
    setShowCampaignDropdown(true);
    if (!value.trim() || value.trim() === 'All Campaigns') {
      setSelectedCampaign('');
    }
  };

  const handleCampaignInputFocus = () => {
    if (campaigns.length > 0) {
      setShowCampaignDropdown(true);
    }
  };

  // Update campaign search term when selectedCampaign changes (e.g., from URL or initial load)
  useEffect(() => {
    if (selectedCampaign && campaigns.length > 0) {
      const campaign = campaigns.find(c => c.id.toString() === selectedCampaign.toString());
      if (campaign && campaignSearchTerm !== campaign.name) {
        setCampaignSearchTerm(campaign.name);
      }
    } else if (!selectedCampaign) {
      setCampaignSearchTerm('All Campaigns');
    }
  }, [selectedCampaign, campaigns]);

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setSelectedSubmission(null);
    setShowDetailModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteSubmission = async (submission) => {
    const name = submission.form_data?.name || submission.form_data?.email || 'this submission';
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete the submission for "<strong>${name}</strong>".<br><br>This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/indemnity-forms/submissions/${submission.id}`);
        await Swal.fire('Deleted!', 'The submission has been deleted.', 'success');
        fetchSubmissions();
      } catch (err) {
        await Swal.fire('Error!', err.response?.data?.error || 'Failed to delete the submission.', 'error');
        console.error('Delete submission error:', err);
      }
    }
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const headers = ['Name', 'Email', 'Phone', 'Campaign/Page', 'Date Signed', 'Date Submission', 'Read Campaign', 'Confirmed Declaration', 'Wants Promotional Material'];
    
    const rows = submissions.map(sub => [
      sub.form_data?.name || '',
      sub.form_data?.email || '',
      sub.form_data?.phone || '',
      sub.campaign_name || sub.indemnity_page_title || 'Standalone Page',
      sub.date_signed,
      formatDate(sub.created_at),
      sub.has_read_campaign ? 'Yes' : 'No',
      sub.has_confirmed_declaration ? 'Yes' : 'No',
      sub.wants_promotional_material ? 'Yes' : 'No'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `indemnity_submissions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection="INDEMNITY">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white mb-6">INDEMNITY FORM SUBMISSIONS</h1>
      </div>

      {/* Filters - same layout as Campaign List & Contact Messages */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Campaign Filter */}
          <div className="relative" ref={campaignDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Campaign
            </label>
            <div className="flex">
              <input
                ref={campaignInputRef}
                type="text"
                value={campaignSearchTerm}
                onChange={handleCampaignInputChange}
                onFocus={handleCampaignInputFocus}
                placeholder="Search campaigns..."
                className={`flex-1 px-3 py-2 border border-gray-300 ${
                  selectedCampaign ? 'rounded-l-md' : 'rounded-md'
                } focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500`}
              />
              {selectedCampaign && (
                <button
                  type="button"
                  onClick={handleCampaignClear}
                  className="flex items-center px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  title="Clear campaign filter"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {showCampaignDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredCampaigns.length === 0 && !isAllCampaigns ? (
                  <div className="px-4 py-3 text-sm text-gray-500">No campaigns found</div>
                ) : (
                  <>
                    {isAllCampaigns && (
                      <button
                        type="button"
                        onClick={() => handleCampaignSelect('', 'All Campaigns')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          !selectedCampaign ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        All Campaigns
                      </button>
                    )}
                    {filteredCampaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        type="button"
                        onClick={() => handleCampaignSelect(campaign.id, campaign.name)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          selectedCampaign === campaign.id.toString()
                            ? 'bg-teal-50 text-teal-700 font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {campaign.name}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, phone..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Search
              </button>
            </form>
          </div>

          {/* Clear filters / Export */}
          <div className="flex items-end gap-2">
            {(searchTerm || selectedCampaign) && (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); handleCampaignClear(); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Clear filters
              </button>
            )}
            <button
              onClick={exportToCSV}
              disabled={submissions.length === 0}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              Export to CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 text-sm text-gray-600">
          {total === 0
            ? 'No submissions'
            : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}-${Math.min(currentPage * PAGE_SIZE, total)} of ${total} submissions`}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  CONTACT INFO
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  CAMPAIGN/PAGE
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  DATE SIGNED
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  DATE SUBMISSION
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{submission.form_data?.name || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{submission.form_data?.email || 'N/A'}</div>
                    {submission.form_data?.phone && (
                      <div className="text-xs text-gray-500 mt-0.5">{submission.form_data.phone}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {submission.campaign_name || submission.indemnity_page_title || 'Standalone Page'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {submission.date_signed || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(submission.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        type="button"
                        onClick={() => openDetailModal(submission)}
                        className="text-teal-600 hover:text-teal-800"
                        title="View Details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSubmission(submission)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && submissions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm || selectedCampaign
                  ? 'No submissions match your search.'
                  : 'No indemnity form submissions yet.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        <div className="px-6 py-4 flex justify-between items-center border-t border-gray-200">
          <div />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Previous page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">
                Page {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Next page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

        {/* Detail Modal - Rendered via Portal */}
        {showDetailModal && selectedSubmission && ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[9999] p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                e.preventDefault();
                closeDetailModal();
              }
            }}
          >
            <div
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="bg-white border-b px-6 py-4 flex justify-between items-center flex-shrink-0">
                <h3 className="text-2xl font-bold text-gray-900">Submission Details</h3>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-gray-500 p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {/* Campaign/Page Info */}
                  <div className="border-b pb-3">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">
                      {selectedSubmission.campaign_name ? 'Campaign' : 'Indemnity Landing Page'}
                    </h4>
                    <p className="text-gray-700">
                      {selectedSubmission.campaign_name || selectedSubmission.indemnity_page_title || 'Standalone Page'}
                    </p>
                    {selectedSubmission.indemnity_page_slug && (
                      <p className="text-sm text-gray-500">Slug: {selectedSubmission.indemnity_page_slug}</p>
                    )}
                    <p className="text-sm text-gray-500">Submitted: {formatDate(selectedSubmission.created_at)}</p>
                  </div>

                  {/* Contact Information */}
                  <div className="border-b pb-3">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="font-medium">Name:</span> {selectedSubmission.form_data?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {selectedSubmission.form_data?.email || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {selectedSubmission.form_data?.phone || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Custom Form Fields */}
                  {selectedSubmission.form_data && Object.keys(selectedSubmission.form_data).some(key => !['name', 'email', 'phone'].includes(key)) && (
                    <div className="border-b pb-3">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Additional Information</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedSubmission.form_data).map(([key, value]) => {
                          if (['name', 'email', 'phone'].includes(key)) return null;
                          return (
                            <div key={key}>
                              <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              {Array.isArray(value) ? value.join(', ') : value || 'N/A'}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Indemnity Confirmations */}
                  <div className="border-b pb-3">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Indemnity Confirmations</h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className={`inline-block w-3 h-3 mr-2 rounded-full ${selectedSubmission.has_read_campaign ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span>Has read campaign: {selectedSubmission.has_read_campaign ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className={`inline-block w-3 h-3 mr-2 rounded-full ${selectedSubmission.has_confirmed_declaration ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span>Confirmed declaration: {selectedSubmission.has_confirmed_declaration ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className={`inline-block w-3 h-3 mr-2 rounded-full ${selectedSubmission.wants_promotional_material ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        <span>Wants promotional material: {selectedSubmission.wants_promotional_material ? 'Yes' : 'No'}</span>
                      </div>
                      <div>
                        <span className="font-medium">Date Signed:</span> {selectedSubmission.date_signed || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Technical Info */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Technical Information</h4>
                    <div className="text-sm space-y-1">
                      <div><span className="font-medium">IP Address:</span> {selectedSubmission.ip_address || 'N/A'}</div>
                      <div><span className="font-medium">User Agent:</span> <span className="text-xs break-all">{selectedSubmission.user_agent || 'N/A'}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border-t px-6 py-4 flex justify-end flex-shrink-0">
                <button
                  onClick={closeDetailModal}
                  className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </AdminLayout>
  );
}

export default IndemnityFormSubmissions;

